/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
/**
 * UnifiedCourseCard
 *
 * @file UnifiedCourseCard.jsx
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Menu, Typography } from '@mui/material';
import { BUTTON_TYPES } from '../../common/constants';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Download from '../download/Download';
import {
  CourseHeaderHotspot,
  CourseHeaderIcon,
  CourseHeaderIconCtaButton,
  CourseHeaderInviteStudentsButton,
  CourseHeaderMenuItem,
  CourseHeaderMenuList,
  CourseHeaderMenuPaper,
  CourseHeaderSecondaryButton,
  CourseHeaderUnifiedActions,
  CourseHeaderUnifiedBadge,
  CourseHeaderUnifiedBookTitle,
  CourseHeaderUnifiedCard,
  CourseHeaderUnifiedCardBody,
  CourseHeaderUnifiedCardMain,
  CourseHeaderUnifiedContent,
  CourseHeaderUnifiedCoverContainer,
  CourseHeaderUnifiedCoverImage,
  CourseHeaderUnifiedCoverInfoButton,
  CourseHeaderUnifiedCourseTitle,
  CourseHeaderUnifiedDateRange,
  CourseHeaderUnifiedDownloadRoot,
  CourseHeaderUnifiedFooter,
  CourseHeaderUnifiedMetaRow,
  CourseHeaderUnifiedRoot
} from './styles/CourseHeaderStyles';

const UnifiedCourseCard = (props) => {
  const {
    course = {},
    book = {},
    config = {},
    callback = {}
  } = props;

  const {
    showCourseTitle = true,
    showBookTitle = true,
    showBookCover = true,
    showTitleInfo = true,
    showDownload = false,
    showMetaData = true,
    showBookActions = true,
    showCourseMenu = true,
    showInviteStudents = true
  } = config;

  const { handleMenuOpenChange } = callback;
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    handleMenuOpenChange?.();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderTitleInfo = () => {
    if (!showTitleInfo || !book.titleInfo) {
      return null;
    }

    const { tooltip, callback: bookTitleInfoCallback, showHotspot = false } = book.titleInfo;

    return (
      <TooltipWrapper tooltip={tooltip || ''} placement="top">
        <CourseHeaderUnifiedCoverInfoButton
          onClick={() => {
            bookTitleInfoCallback?.();
          }}
          aria-haspopup="dialog"
        >
          {showHotspot && <CourseHeaderHotspot />}
          <CourseHeaderIcon name="InfoIcon" />
        </CourseHeaderUnifiedCoverInfoButton>
      </TooltipWrapper>
    );
  };

  const renderBookCover = () => {
    if (!showBookCover || !book.coverImage) {
      return null;
    }

    const { progress, status, onCancelClick, onBookClick } = book?.titleInfo || {};

    return (
      <CourseHeaderUnifiedCoverContainer>
        {showDownload ? (
          <CourseHeaderUnifiedDownloadRoot>
            <Download
              backgroundImage={book?.coverImage}
              progress={progress}
              downloadStatus={status}
              onCancelClick={onCancelClick}
              onClick={onBookClick}
            />
          </CourseHeaderUnifiedDownloadRoot>
        ) : (
          <CourseHeaderUnifiedCoverImage src={book.coverImage} alt="Book cover" />
        )}
        {renderTitleInfo()}
      </CourseHeaderUnifiedCoverContainer>
    );
  };

  const renderMetaData = () => {
    if (!showMetaData || !course.metaData?.length) {
      return null;
    }

    const badgeItems = course.metaData.filter((meta) => meta.badge && meta.info);
    const dateItems = course.metaData.filter((meta) => !meta.badge && meta.info);

    if (!badgeItems.length && !dateItems.length) {
      return null;
    }

    return (
      <CourseHeaderUnifiedMetaRow>
        {badgeItems.map((meta) => (
          <CourseHeaderUnifiedBadge key={meta.label || meta.info}>
            {meta.info}
          </CourseHeaderUnifiedBadge>
        ))}
        {dateItems.length === 1 && (
          <CourseHeaderUnifiedDateRange component="span">
            {dateItems[0].info}
          </CourseHeaderUnifiedDateRange>
        )}
        {dateItems.length > 1 && (
          <CourseHeaderUnifiedDateRange component="span">
            {dateItems[0].info} – {dateItems[1].info}
          </CourseHeaderUnifiedDateRange>
        )}
      </CourseHeaderUnifiedMetaRow>
    );
  };

  const renderIconCtaButton = (action, key) => {
    if (!action?.callback || !action.text) {
      return null;
    }

    return (
      <CourseHeaderIconCtaButton
        key={key}
        onClick={() => action.callback(action)}
        startIcon={action.iconName ? <CourseHeaderIcon name={action.iconName} /> : null}
      >
        {action.text}
      </CourseHeaderIconCtaButton>
    );
  };

  const renderBookAction = (action) => {
    if (!action.callback) {
      return null;
    }

    if (action.type === BUTTON_TYPES.BUTTON) {
      return (
        <CourseHeaderSecondaryButton
          key={action.text}
          onClick={action.callback}
        >
          {action.text}
        </CourseHeaderSecondaryButton>
      );
    }

    if (!action.type && action.text) {
      return renderIconCtaButton(action, action.text);
    }

    return null;
  };

  const renderCourseMenu = () => {
    if (!course.courseMenu || !course.courseMenu.length) {
      return null;
    }

    return (
      <Menu
        id="unified-course-menu"
        anchorEl={menuAnchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slots={{
          paper: CourseHeaderMenuPaper,
          list: CourseHeaderMenuList
        }}
      >
        {course.courseMenu.map((item) => (
          <CourseHeaderMenuItem
            key={item.label}
            disableRipple
            disableGutters
            hasDivider={item.divider}
            onClick={() => {
              handleMenuClose();
              item.callback(item);
            }}
          >
            <Typography>{item.label}</Typography>
          </CourseHeaderMenuItem>
        ))}
      </Menu>
    );
  };

  const renderSettingsMenuTrigger = () => {
    if (!showCourseMenu || !course.courseMenu?.length) {
      return null;
    }

    return (
      <Fragment>
        <CourseHeaderIconCtaButton
          onClick={handleMenuOpen}
          startIcon={<CourseHeaderIcon name="SettingsIcon" />}
        >
          {course.textInfo.settingsLabel}
        </CourseHeaderIconCtaButton>
        {renderCourseMenu()}
      </Fragment>
    );
  };

  const renderActions = () => {
    const bookActions = showBookActions ? book?.actions || [] : [];
    const settingsMenuTrigger = renderSettingsMenuTrigger();

    if (!bookActions.length && !settingsMenuTrigger) {
      return null;
    }

    return (
      <CourseHeaderUnifiedActions>
        {bookActions.map((action) => renderBookAction(action))}
        {settingsMenuTrigger}
      </CourseHeaderUnifiedActions>
    );
  };

  const renderInviteStudents = () => {
    if (!showInviteStudents || !course.inviteStudents) {
      return null;
    }

    return (
      <CourseHeaderInviteStudentsButton onClick={course.inviteStudents.callback}>
        {course.inviteStudents.text}
      </CourseHeaderInviteStudentsButton>
    );
  };

  const inviteStudents = renderInviteStudents();

  return (
    <CourseHeaderUnifiedRoot>
      <CourseHeaderUnifiedCard>
        <CourseHeaderUnifiedCardMain>
          <CourseHeaderUnifiedCardBody>
            {renderBookCover()}
            <CourseHeaderUnifiedContent>
              {showCourseTitle && course.title && (
                <CourseHeaderUnifiedCourseTitle>{course.title}</CourseHeaderUnifiedCourseTitle>
              )}
              {renderMetaData()}
              {showBookTitle && book.title && (
                <CourseHeaderUnifiedBookTitle>{book.title}</CourseHeaderUnifiedBookTitle>
              )}
            </CourseHeaderUnifiedContent>
            {renderActions()}
          </CourseHeaderUnifiedCardBody>
          {inviteStudents && (
            <CourseHeaderUnifiedFooter>
              {inviteStudents}
            </CourseHeaderUnifiedFooter>
          )}
        </CourseHeaderUnifiedCardMain>
      </CourseHeaderUnifiedCard>
    </CourseHeaderUnifiedRoot>
  );
};

UnifiedCourseCard.propTypes = {
  course: PropTypes.object,
  book: PropTypes.object,
  config: PropTypes.object,
  callback: PropTypes.shape({
    handleMenuOpenChange: PropTypes.func
  })
};

UnifiedCourseCard.defaultProps = {
  course: {},
  book: {},
  config: {},
  callback: {}
};

export default UnifiedCourseCard;
